<template>
  <div class="trn-courses">

    <div class="page-header">
      <div>
        <h5 class="page-title">Training Courses</h5>
        <p class="page-sub">Create, manage and publish your training catalogue</p>
      </div>
      <div class="header-actions">
        <template v-if="activeTab === 'courses'">
          <button class="btn-export" @click="openExportModal('csv')" :disabled="exportLoading">
            <font-awesome-icon :icon="exportLoading && pendingExportFmt==='csv' ? 'spinner' : 'file-csv'" :spin="exportLoading && pendingExportFmt==='csv'" /> CSV
          </button>
          <button class="btn-export" @click="openExportModal('pdf')" :disabled="exportLoading">
            <font-awesome-icon :icon="exportLoading && pendingExportFmt==='pdf' ? 'spinner' : 'file-pdf'" :spin="exportLoading && pendingExportFmt==='pdf'" /> PDF
          </button>
          <router-link :to="$url('/hr/training/courses/new')" class="btn-primary">
            <font-awesome-icon icon="plus" class="me-1" /> New Course
          </router-link>
        </template>
        <button v-else class="btn-primary" @click="openCatModal()">
          <font-awesome-icon icon="plus" class="me-1" /> New Category
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tab-row">
      <button :class="['tab-btn', activeTab === 'courses' ? 'tab-btn--active' : '']" @click="activeTab = 'courses'">Courses</button>
      <button :class="['tab-btn', activeTab === 'categories' ? 'tab-btn--active' : '']" @click="activeTab = 'categories'; loadCategories()">Categories</button>
    </div>

    <!-- ── COURSES TAB ── -->
    <template v-if="activeTab === 'courses'">
      <div class="filter-bar">
        <input v-model="search" class="filter-input" placeholder="Search by title…" @input="debouncedLoad" />
        <select v-model="filterStatus" class="filter-select" @change="page = 1; loadCourses()">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_review">In Review</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select v-model="filterType" class="filter-select" @change="page = 1; loadCourses()">
          <option value="">All Types</option>
          <option value="mandatory">Mandatory</option>
          <option value="recommended">Recommended</option>
          <option value="optional">Optional</option>
        </select>
        <div class="per-page-wrap">
          <label class="per-page-label">Show</label>
          <select class="per-page-select" v-model.number="length" @change="page = 1; loadCourses()">
            <option v-for="n in [5, 10, 20, 50, 100, 200, 500, 1000]" :key="n" :value="n">{{ n }}</option>
          </select>
          <span class="per-page-label">per page</span>
        </div>
      </div>

      <div class="table-card">
        <div v-if="loading" class="table-loader"><div class="spinner"></div></div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>Code</th><th>Title</th><th>Type</th><th>Category</th>
              <th>Duration</th><th>Status</th><th>Version</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!courses.length">
              <td colspan="8" class="table-empty">No courses found.</td>
            </tr>
            <tr v-for="c in courses" :key="c.id">
              <td><code class="code-badge">{{ c.code }}</code></td>
              <td class="fw-600">{{ c.title }}</td>
              <td><span :class="['type-pill', 'type-pill--' + c.course_type]">{{ c.course_type }}</span></td>
              <td>{{ c.category?.name ?? '—' }}</td>
              <td>{{ c.est_minutes ? c.est_minutes + ' min' : '—' }}</td>
              <td><span :class="['status-pill', 'status-pill--' + c.status]">{{ c.status.replace('_',' ') }}</span></td>
              <td>v{{ c.version }}</td>
              <td class="action-col">
                <router-link :to="$url('/hr/training/courses/' + c.id)" class="action-btn" title="Edit">
                  <font-awesome-icon icon="edit" />
                </router-link>
                <button v-if="c.status === 'draft'" class="action-btn action-btn--green" @click="publish(c)" title="Publish">
                  <font-awesome-icon icon="check" />
                </button>
                <button v-if="c.status === 'published'" class="action-btn action-btn--amber" @click="archive(c)" title="Archive">
                  <font-awesome-icon icon="archive" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="pagination.last_page > 1" class="pagination-bar">
          <span class="pag-count">
            {{ ((pagination.current_page - 1) * length) + 1 }}–{{ Math.min(pagination.current_page * length, pagination.total) }} of {{ pagination.total }}
          </span>
          <div class="pag-btns">
            <button class="pag-btn" :disabled="pagination.current_page <= 1" @click="goPage(1)" title="First">«</button>
            <button class="pag-btn" :disabled="pagination.current_page <= 1" @click="goPage(pagination.current_page - 1)">‹</button>
            <button
              v-for="p in pageRange"
              :key="p"
              :class="['pag-btn', p === pagination.current_page ? 'pag-btn--active' : '']"
              @click="goPage(p)"
            >{{ p }}</button>
            <button class="pag-btn" :disabled="pagination.current_page >= pagination.last_page" @click="goPage(pagination.current_page + 1)">›</button>
            <button class="pag-btn" :disabled="pagination.current_page >= pagination.last_page" @click="goPage(pagination.last_page)" title="Last">»</button>
          </div>
        </div>
      </div>
    </template>

    <!-- ── CATEGORIES TAB ── -->
    <template v-else>
      <div class="table-card">
        <div v-if="catLoading" class="table-loader"><div class="spinner"></div></div>
        <table v-else class="data-table">
          <thead>
            <tr><th>Name</th><th>Sub-categories</th><th>Courses</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-if="!categories.length">
              <td colspan="4" class="table-empty">No categories yet. Click "New Category" to add one.</td>
            </tr>
            <tr v-for="cat in categories" :key="cat.id">
              <td class="fw-600">{{ cat.name }}</td>
              <td>
                <span v-if="!cat.children || !cat.children.length" class="muted">—</span>
                <span v-else class="sub-list">
                  <span v-for="(ch, i) in cat.children" :key="ch.id">
                    {{ ch.name }}<span v-if="i < cat.children.length - 1">, </span>
                  </span>
                </span>
              </td>
              <td>{{ cat.courses_count ?? '—' }}</td>
              <td class="action-col">
                <button class="action-btn" title="Add sub-category" @click="openCatModal(cat)">
                  <font-awesome-icon icon="folder-plus" />
                </button>
                <button class="action-btn" title="Edit" @click="openCatModal(null, cat)">
                  <font-awesome-icon icon="edit" />
                </button>
                <button class="action-btn action-btn--red" title="Delete" @click="deleteCategory(cat)">
                  <font-awesome-icon icon="trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ── CONFIRM MODAL ── -->
    <div v-if="confirm.show" class="modal-backdrop" @click.self="confirm.show = false">
      <div class="modal-box confirm-box">
        <div class="confirm-icon-wrap" :class="'confirm-icon-wrap--' + confirm.variant">
          <font-awesome-icon :icon="confirm.variant === 'publish' ? 'upload' : confirm.variant === 'archive' ? 'archive' : 'trash'" />
        </div>
        <h3 class="confirm-title">{{ confirm.title }}</h3>
        <p class="confirm-body">{{ confirm.message }}</p>
        <div class="confirm-actions">
          <button class="btn-outline" @click="confirm.show = false">Cancel</button>
          <button :class="['btn-confirm', 'btn-confirm--' + confirm.variant]" :disabled="confirm.loading" @click="confirm.onOk">
            <span v-if="confirm.loading" class="spinner-sm"></span>
            {{ confirm.loading ? 'Please wait…' : confirm.okLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── CATEGORY MODAL ── -->
    <div v-if="catModal.show" class="modal-backdrop" @click.self="closeCatModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ catModal.editTarget ? 'Edit Category' : (catModal.parent ? 'New Sub-category under "' + catModal.parent.name + '"' : 'New Category') }}</span>
          <button class="modal-close" @click="closeCatModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <label class="field-label">Name <span class="req">*</span></label>
          <input v-model="catModal.name" type="text" class="field-input" placeholder="e.g. Health & Safety" ref="catNameInput" @keyup.enter="saveCategory" />
          <p v-if="catModal.error" class="cat-error">{{ catModal.error }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeCatModal">Cancel</button>
          <button class="btn-primary" :disabled="catModal.saving" @click="saveCategory">
            <span v-if="catModal.saving" class="spinner-sm"></span>
            {{ catModal.saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'TrainingCourses',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      activeTab: 'courses',
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportLoading: false,
      exportColumnDefs: [
        { key: 'code',     label: 'Code',     pdfWidth: 70 },
        { key: 'title',    label: 'Title',    pdfWidth: 200 },
        { key: 'type',     label: 'Type',     pdfWidth: 90 },
        { key: 'category', label: 'Category', pdfWidth: 120 },
        { key: 'duration', label: 'Duration', pdfWidth: 80 },
        { key: 'status',   label: 'Status',   pdfWidth: 90 },
        { key: 'version',  label: 'Version',  pdfWidth: 70 },
      ],
      // courses
      loading: false,
      courses: [],
      pagination: { current_page: 1, last_page: 1, total: 0 },
      search: '',
      filterStatus: '',
      filterType: '',
      page: 1,
      length: 5,
      debounceTimer: null,
      // categories
      catLoading: false,
      categories: [],
      catModal: { show: false, name: '', parent: null, editTarget: null, saving: false, error: '' },
      confirm: { show: false, title: '', message: '', okLabel: '', variant: 'publish', loading: false, onOk: null },
    };
  },
  computed: {
    pageRange() {
      const cur  = this.pagination.current_page || 1;
      const last = this.pagination.last_page    || 1;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },
  },
  created() { this.loadCourses(); },
  methods: {
    // ── Export ───────────────────────────────────────────────────────────────
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(row, key) {
      switch (key) {
        case 'code':     return row.code || '—';
        case 'title':    return row.title || '—';
        case 'type':     return row.course_type || '—';
        case 'category': return row.category?.name || '—';
        case 'duration': return row.est_minutes ? row.est_minutes + ' min' : '—';
        case 'status':   return row.status?.replace('_', ' ') || '—';
        case 'version':  return row.version ? 'v' + row.version : '—';
        default:         return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const { data } = await axios.get('/training/courses', {
          params: { search: this.search, status: this.filterStatus, course_type: this.filterType, page: 1, per_page: 10000 },
        });
        const d    = data.data;
        const rows = d.data ?? d ?? [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `training-courses-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Training Courses', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`training-courses-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally   { this.exportLoading = false; }
    },

    // ── Courses ──────────────────────────────────────────────────────────────
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadCourses(), 400);
    },
    async loadCourses() {
      this.loading = true;
      try {
        const { data } = await axios.get('/training/courses', {
          params: { search: this.search, status: this.filterStatus, course_type: this.filterType, page: this.page, per_page: this.length },
        });
        const p = data.data;
        this.courses    = p.data ?? p;
        this.pagination = { current_page: p.current_page, last_page: p.last_page, total: p.total ?? 0 };
      } catch { this.toast.error('Failed to load courses.'); }
      finally { this.loading = false; }
    },
    goPage(p) { this.page = p; this.loadCourses(); },
    publish(course) {
      this.confirm = {
        show: true, variant: 'publish', loading: false,
        title: 'Publish Course',
        message: `"${course.title}" will be visible to all learners once published. This cannot be undone.`,
        okLabel: 'Yes, Publish',
        onOk: async () => {
          this.confirm.loading = true;
          try {
            await axios.post(`/training/courses/${course.id}/publish`);
            this.toast.success('Course published.');
            this.confirm.show = false;
            this.loadCourses();
          } catch (e) {
            this.confirm.loading = false;
            this.toast.error(e.response?.data?.message ?? 'Failed to publish.');
          }
        },
      };
    },
    archive(course) {
      this.confirm = {
        show: true, variant: 'archive', loading: false,
        title: 'Archive Course',
        message: `"${course.title}" will be hidden from the catalogue. Existing enrolments are not affected.`,
        okLabel: 'Yes, Archive',
        onOk: async () => {
          this.confirm.loading = true;
          try {
            await axios.post(`/training/courses/${course.id}/archive`);
            this.toast.success('Course archived.');
            this.confirm.show = false;
            this.loadCourses();
          } catch (e) {
            this.confirm.loading = false;
            this.toast.error(e.response?.data?.message ?? 'Failed to archive.');
          }
        },
      };
    },

    // ── Categories ───────────────────────────────────────────────────────────
    async loadCategories() {
      this.catLoading = true;
      try {
        const { data } = await axios.get('/training/categories');
        this.categories = data.data ?? [];
      } catch { this.toast.error('Failed to load categories.'); }
      finally { this.catLoading = false; }
    },
    openCatModal(parent = null, editTarget = null) {
      this.catModal = {
        show: true,
        name: editTarget ? editTarget.name : '',
        parent,
        editTarget,
        saving: false,
        error: '',
      };
      this.$nextTick(() => this.$refs.catNameInput?.focus());
    },
    closeCatModal() {
      this.catModal.show = false;
    },
    async saveCategory() {
      const name = this.catModal.name.trim();
      if (!name) { this.catModal.error = 'Name is required.'; return; }
      this.catModal.saving = true;
      this.catModal.error  = '';
      try {
        if (this.catModal.editTarget) {
          await axios.put(`/training/categories/${this.catModal.editTarget.id}`, { name });
          this.toast.success('Category updated.');
        } else {
          await axios.post('/training/categories', {
            name,
            parent_id: this.catModal.parent?.id ?? null,
          });
          this.toast.success('Category created.');
        }
        this.closeCatModal();
        this.loadCategories();
      } catch (e) {
        this.catModal.error = e.response?.data?.message ?? 'Failed to save.';
      } finally {
        this.catModal.saving = false;
      }
    },
    deleteCategory(cat) {
      this.confirm = {
        show: true, variant: 'delete', loading: false,
        title: 'Delete Category',
        message: `Delete "${cat.name}"? This cannot be undone.`,
        okLabel: 'Delete',
        onOk: async () => {
          this.confirm.loading = true;
          try {
            await axios.delete(`/training/categories/${cat.id}`);
            this.toast.success('Category deleted.');
            this.confirm.show = false;
            this.loadCategories();
          } catch (e) {
            this.confirm.loading = false;
            this.toast.error(e.response?.data?.message ?? 'Failed to delete.');
          }
        },
      };
    },
  },
};
</script>

<style scoped>
.trn-courses { padding-bottom:32px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; gap:10px; }
.me-1 { margin-right:4px; }

/* Tabs */
.tab-row { display:flex; gap:4px; border-bottom:1.5px solid #e4eaf2; margin-bottom:20px; }
.tab-btn { background:none; border:none; padding:10px 20px; font-size:13px; font-weight:500; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-1.5px; }
.tab-btn:hover { color:#1a2b45; }
.tab-btn--active { color:#004AAD; border-bottom-color:#004AAD; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { padding:9px 14px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; min-width:220px; flex:1; }
.filter-select { padding:9px 14px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.table-loader { display:flex; align-items:center; justify-content:center; height:200px; }
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:12px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr:last-child td { border-bottom:none; }
.table-empty { text-align:center; color:#aab8cc; padding:40px !important; }
.fw-600 { font-weight:600; }
.muted { color:#aab8cc; }
.sub-list { font-size:12px; color:#7a90b0; }

.code-badge { background:#f1f5f9; color:#475569; padding:2px 7px; border-radius:4px; font-size:11px; }
.type-pill, .status-pill { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; text-transform:capitalize; }
.type-pill--mandatory    { background:#fee2e2; color:#dc2626; }
.type-pill--recommended  { background:#dbeafe; color:#1d4ed8; }
.type-pill--optional     { background:#dcfce7; color:#15803d; }
.status-pill--draft      { background:#f1f5f9; color:#475569; }
.status-pill--in_review  { background:#fef9c3; color:#854d0e; }
.status-pill--published  { background:#dcfce7; color:#15803d; }
.status-pill--archived   { background:#f1f5f9; color:#94a3b8; }

.action-col { text-align:right; white-space:nowrap; }
.action-btn { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border:none; border-radius:7px; background:#f1f5f9; color:#64748b; cursor:pointer; margin-left:4px; text-decoration:none; font-size:13px; }
.action-btn:hover { background:#e2e8f0; }
.action-btn--green { background:#dcfce7; color:#15803d; }
.action-btn--green:hover { background:#bbf7d0; }
.action-btn--amber { background:#fff7ed; color:#c2410c; }
.action-btn--amber:hover { background:#fed7aa; }
.action-btn--red { background:#fee2e2; color:#dc2626; }
.action-btn--red:hover { background:#fecaca; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-top:1px solid #f0f4f8; flex-wrap:wrap; gap:8px; }
.pag-count { font-size:12px; color:#7a90b0; }
.pag-btns  { display:flex; gap:4px; }
.pag-btn   { min-width:30px; height:30px; border:1.5px solid #dde5f0; border-radius:6px; background:#fff; color:#1a2b45; font-size:12px; cursor:pointer; padding:0 6px; }
.pag-btn:hover:not(:disabled) { background:#f0f4f8; }
.pag-btn:disabled { opacity:.35; cursor:not-allowed; }
.pag-btn--active { background:#004AAD; color:#fff; border-color:#004AAD; }
.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:12px; color:#7a90b0; }
.per-page-select { height:32px; border:1.5px solid #dde5f0; border-radius:7px; padding:0 8px; font-size:12px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }
.per-page-select:focus { border-color:#004AAD; }

/* Modal */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:420px; box-shadow:0 20px 60px rgba(0,0,0,.15); }

/* Confirm modal */
.confirm-box { padding:32px 28px 24px; text-align:center; }
.confirm-icon-wrap { width:56px; height:56px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:22px; margin:0 auto 18px; }
.confirm-icon-wrap--publish { background:#dcfce7; color:#15803d; }
.confirm-icon-wrap--archive { background:#fff7ed; color:#c2410c; }
.confirm-icon-wrap--delete  { background:#fee2e2; color:#dc2626; }
.confirm-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.confirm-body  { font-size:13px; color:#64748b; margin:0 0 24px; line-height:1.55; }
.confirm-actions { display:flex; justify-content:center; gap:10px; }
.btn-confirm { display:inline-flex; align-items:center; gap:6px; border:none; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; cursor:pointer; }
.btn-confirm:disabled { opacity:.6; cursor:not-allowed; }
.btn-confirm--publish { background:#15803d; color:#fff; }
.btn-confirm--publish:hover:not(:disabled) { background:#166534; }
.btn-confirm--archive { background:#c2410c; color:#fff; }
.btn-confirm--archive:hover:not(:disabled) { background:#9a3412; }
.btn-confirm--delete  { background:#dc2626; color:#fff; }
.btn-confirm--delete:hover:not(:disabled)  { background:#b91c1c; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:14px; font-weight:700; color:#1a2b45; }
.modal-close { background:none; border:none; font-size:16px; color:#94a3b8; cursor:pointer; padding:4px; }
.modal-close:hover { color:#1a2b45; }
.modal-body { padding:20px; display:flex; flex-direction:column; gap:8px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:14px 20px 18px; border-top:1px solid #f0f4f8; }

.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.cat-error { font-size:12px; color:#e03535; margin:0; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; text-decoration:none; }
.btn-primary:hover:not(:disabled) { background:#0056d2; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-export { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; white-space:nowrap; font-family:inherit; }
.btn-export:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.btn-export:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }
</style>
